/* eslint-disable max-statements */
import {createBrowserHistory} from 'history';
import routers, {ServerConfig, buildPath} from './../config/routers';
import {ErrorCode, WizKnownError} from '@/sdk/common/error';
import assert from 'assert';
import AwaitLock from 'await-lock';
import {WizRequestOptions} from '../interface';
import AccountServer, {accountServerObj} from './server/account_server';
import KnowledgeServer from './server/knowledge_server';
import KuShareServer from './server/kuShare';
import ServerBase, {serverBaseObj} from './server/server_base';
import BaikeServer from './server/baike_server';
import * as store from '../store/index';
import {ERROR_CODE} from './common/const';
import {get} from 'lodash-es';
import {getCurPathWithoutTicket} from '@/utils/urlUtils';
import {feLoggerSetUid} from '@/buried';
import weirwood from '@/weirwood';
import {addFrontTraceInfo} from './common/addFrontTraceInfo';
import {getUrlParams, removeSearchParamNoRefresh} from '@/utils/kuQuery';
import KU_QUERY from '@/constants/common/searchParamKeys';
import {feLogger, reportTokenRetryByGetKsServer} from '@/buried';
const history = createBrowserHistory();

const setFeLogger = (userGuid: string) => {
    if (userGuid) {
        feLoggerSetUid(userGuid);
        weirwood && weirwood.error.setContext({
            userId: userGuid,
        }, {
            persist: true,
        });
    }
};

export class SDK {
    as: AccountServer;

    ksMap = new Map<string, KnowledgeServer>();

    ksServerLock = new AwaitLock();

    kuShare: KuShareServer;

    serverBase: ServerBase; // 普通调用接口场景使用

    baikeServer: BaikeServer;

    constructor() {
        this.as = accountServerObj;
        this.as.setInvalidTokenHandler(this.invalidTokenAsHandler);
        this.kuShare = new KuShareServer();
        this.serverBase = serverBaseObj;
        this.baikeServer = new BaikeServer();
        this.as.addFrontTraceInfo = addFrontTraceInfo.bind(this);

    }

    get isLoggedIn() {
        return !!this.as.user;
    }

    get user() {
        return this.as.user;
    }

    invalidTokenAsHandler = async (error?: WizKnownError) => {
        if (!error) {
            return this.as.user?.token || '';
        }
        const errorCode = Number(error.code);
        console.log('日志：invalidTokenAsHandler触发autoLogin: ----------------', '', window.location.href);
        const user = await this.as.autoLogin('', {
            noCheckExists: true,
            onlyRefreshToken: (errorCode === ERROR_CODE.uuapTokenInvalid || errorCode === ERROR_CODE.ugateTokenInvalid),
            uuapJumpUrl: get(error, 'result.location', ''),
        });
        if (user && user.token) {
            return user.token;
        }
        return '';
    };

    invalidTokenKsHandler = async (
        spaceGuid: string,
        groupGuid: string,
        repositoryGuid: string
    ) => {
        const accessToken = await this.as.getWikiAccessToken(
            spaceGuid,
            groupGuid,
            repositoryGuid
        );
        return accessToken;
    };

    saveKsServer = (repositoryData?: any) => {
        const {
            spaceGuid,
            groupGuid,
            repositoryGuid,
            accessToken,
        } = repositoryData;
        assert(accessToken);
        assert(this.as.user);
        const ks = new KnowledgeServer(
            this.as.user,
            spaceGuid,
            groupGuid,
            repositoryGuid,
            accessToken
        );
        ks.addFrontTraceInfo = addFrontTraceInfo.bind(this);
        ks.setInvalidTokenHandler(async (error?: WizKnownError, mergedRequestType?: string) => {
            if (!error) {
                return '';
            }
            // 如果是复合query接口 错误码301 需要重新请求check接口
            if (error.code === ERROR_CODE.wikiTokenInvalid && mergedRequestType) {
                const token = await this.invalidTokenAsHandler(error);
                this.as.user && (this.as.user.token = token);
                return {
                    wikiAccessToken: ks.requestOptions.wikiAccessToken || '',
                    wikiToken: token || '',
                };
            }
            // 非复合query接口返回301 或 复合接口返回308 需要重新获取accessToken
            if ([ERROR_CODE.accessTokenInvalid, ERROR_CODE.wikiTokenInvalid].includes(+error.code)) {
                const token = await this.ksServerInvalidTokenHandler(repositoryData, error);
                return {
                    wikiAccessToken: token || '',
                    wikiToken: this.as.user?.token || '',
                };
            }
            return '';
        });
        this.ksMap.set(repositoryGuid, ks);
    };


    ksServerInvalidTokenHandler = async (repositoryData: Repository, error?: WizKnownError) => {
        const errorCode = Number(error?.code);
        if (error && (errorCode === ERROR_CODE.uuapTokenInvalid || errorCode === ERROR_CODE.ugateTokenInvalid)) {
            console.log('日志：ksServerInvalidTokenHandler触发autoLogin: ----------------', '', window.location.href);
            await this.as.autoLogin('', {
                noCheckExists: true,
                onlyRefreshToken:
                    (errorCode === ERROR_CODE.uuapTokenInvalid || errorCode === ERROR_CODE.ugateTokenInvalid),
                uuapJumpUrl: get(error, 'result.location', ''),
            });
        }
        const {
            spaceGuid,
            groupGuid,
            repositoryGuid,
        } = repositoryData;
        return this.invalidTokenKsHandler(
            spaceGuid,
            groupGuid,
            repositoryGuid
        );
    };

    getKsServer = async (repositoryData?: Repository) => {
        await this.ksServerLock.acquireAsync();
        const repository = repositoryData || store.getData(store.KEYS.CUR_REPOSITORY);
        const {spaceGuid, groupGuid, repositoryGuid} = repository;

        let ks = this.ksMap.get(repositoryGuid);
        if (ks) {
            this.ksServerLock.release();
            return ks;
        }
        try {
            const accessToken = await this.as.getWikiAccessToken(
                spaceGuid,
                groupGuid,
                repositoryGuid
            );
            assert(accessToken);
            assert(this.as.user);

            ks = new KnowledgeServer(
                this.as.user,
                spaceGuid,
                groupGuid,
                repositoryGuid,
                accessToken
            );
            ks.setInvalidTokenHandler(async (error?: WizKnownError) => {
                const token = await this.ksServerInvalidTokenHandler(repository, error);

                feLogger(reportTokenRetryByGetKsServer({
                    token,
                    code: error?.code,
                    message: error?.message,
                }));

                return token;
            });
            this.ksMap.set(repositoryGuid, ks);
        } finally {
            this.ksServerLock.release();
        }
        return ks;
    };

    getDstToken = async () => {
        const origin = ServerConfig.origin;

        try {
            // @dst-todo 缓存逻辑暂时注释，后续再做优化
            // const dataSheetToken = storageUtils.get(DataSheetTokenStorageKey);
            // if (dataSheetToken) {
            //     store.setDstToken(dataSheetToken);
            //     return dataSheetToken;
            // }

            const result = await this.serverBase.request({
                method: 'get',
                // eslint-disable-next-line max-len
                url: `${origin}/api/v1/social/oneaccess/login?reference=${origin}/api/v1/user/me`,
                noFrontTrace: true,
            });

            const token = result?.apiKey || '';
            // @dst-todo 缓存逻辑暂时注释，后续再做优化
            // storageUtils.set(DataSheetTokenStorageKey, token);
            store.setDstToken(token);

            return token;
        } catch (err) {
            console.error(err);
            throw Error('getDstToken Error');
        }
    };

    async autoLogin(options?: WizRequestOptions) {
        if (this.isLoggedIn) {
            assert(false, `user has already login: ${this.user?.id}`);
        }
        try {
            let user = null;

            const isTocPage = window.isTocPage;
            // 对外知识库使用passport的stoken鉴权，对内知识库使用uuap的ticket鉴权
            const ticket =
                isTocPage ? getUrlParams(KU_QUERY.common.login.stoken) : getUrlParams(KU_QUERY.common.login.ticket);
            console.log('日志：autoLogin触发autoLogin: ----------------', ticket, window.location.href);
            user = await this.as.autoLogin(ticket?.toString() || '', options);

            if (user) {
                store.setUser(user);
                const userGuid = user.userGuid;
                feLoggerSetUid(userGuid);
                weirwood && weirwood.error.setContext({
                    userId: userGuid,
                }, {
                    persist: true,
                });
            }
            removeSearchParamNoRefresh(KU_QUERY.common.login.ticket);
            return user;
        } catch (error) {
            console.error(error);
        }
    }

    // eslint-disable-next-line complexity
    async infoQuery({
        spaceGuid,
        groupGuid,
        repositoryGuid,
        docGuid,
        needRepositoryData,
    }: {
        spaceGuid: string;
        groupGuid: string;
        repositoryGuid: string;
        docGuid?: string;
        needRepositoryData?: boolean;
    }) {
        // 获取当前页面的数据表 nodeId
        let nodeId = '';
        try {
            const urlObj = new URL(window.location.href);
            const datasheetId = urlObj.searchParams.get(KU_QUERY.document.tb);
            if (datasheetId) {
                nodeId = datasheetId.split('_')[0];
            }
        } catch (error) {
            console.error(error);
        }

        // 如果没有x-wiki-access
        const ks = this.ksMap.get(repositoryGuid);
        if (!ks || !this.as.user?.token) {
            try {
                const result = await this.as.infoQuery({
                    spaceGuid,
                    groupGuid,
                    repositoryGuid,
                    docGuid,
                    nodeId,
                    needRepositoryData,
                    noAutoLogin: true,
                });
                if (result.user && result.token) {
                    this.as.updateUserInfo(result);
                    setFeLogger(result?.user?.userGuid || '');
                }
                // 记录accessToken
                if (result.accessToken) {
                    this.saveKsServer({
                        spaceGuid,
                        groupGuid,
                        repositoryGuid,
                        accessToken: result.accessToken,
                    });
                }
                return result;
            } catch (error) {
                // sdk token失效，需要获取token
                if ((window as any).isSdkPage && error.code === ERROR_CODE.wikiTokenInvalid) {
                    return new Error('token 失效，请刷新页面');
                }
                if (error.code === ERROR_CODE.wikiTokenInvalid) {
                    // eslint-disable-next-line max-depth
                    try {
                        const ticket = window.isTocPage
                            ? getUrlParams(KU_QUERY.common.login.stoken)
                            : getUrlParams(KU_QUERY.common.login.ticket);
                        console.log('日志：query接口失败, 触发loginWithAuth: ----------------', ticket, window.location.href);
                        const result = await this.as.loginWithAuth(ticket?.toString() || '');
                        // eslint-disable-next-line max-depth
                        if (!result) {
                            return null;
                        }
                        ticket && history.replace(getCurPathWithoutTicket());
                        // eslint-disable-next-line max-depth
                        if (result?.user && result?.token) {
                            this.as.updateUserInfo(result);
                            setFeLogger(result?.user?.userGuid || '');
                        }
                        const data = await this.as.infoQuery({
                            spaceGuid,
                            groupGuid,
                            repositoryGuid,
                            docGuid,
                            needRepositoryData,
                            noAutoLogin: true,
                        });
                        // eslint-disable-next-line max-depth
                        if (data.accessToken) {
                            this.saveKsServer({
                                spaceGuid,
                                groupGuid,
                                repositoryGuid,
                                accessToken: data.accessToken,
                            });
                        }
                        return {
                            ...data,
                            user: result.user,
                        };
                    } catch (error) {
                        return null;
                    }
                }
                if (!this.as.user) {
                    this.autoLogin();
                }
                if (error.code === ErrorCode.userForbidden) {
                    const path = buildPath(routers.Forbidden.path, {});
                    location.href = path;
                }
            }
        }
        // 如果有x-wiki-access
        return ks?.infoQuery({
            docGuid,
            nodeId,
            needRepositoryData,
        });
    }
}

const sdk = new SDK();

export default sdk;
